import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Radio, Icon, Input } from 'semantic-ui-react';
import { ErrorLabel } from 'components';
import { colors } from 'utils/colors';

const map = {
  'WS All-In': {
    percent: 100,
    label: '100% Equities / 0% Bonds',
    icon: 'lightning',
  },
  'WS Adventurous': {
    percent: 80,
    label: '80% Equities / 20% Bonds',
    icon: 'map signs',
  },
  'WS Classic': {
    percent: 60,
    label: '60% Equities / 40% Bonds',
    icon: 'leaf',
  },
  'WS Cautious': {
    percent: 40,
    label: '40% Equities / 60% Bonds',
    icon: 'cloud',
  },
  'WS Playing-It-Safe': {
    percent: 20,
    label: '20% Equities / 80% Bonds',
    icon: 'anchor',
  },
  'Risk-free': {
    icon: 'database',
    description: 'Fixed rate of return',
  },
};

const removeWsForUsVersion = (name) => {
  if ((localStorage.getItem('user_region') || 'CA') === 'CA') return name;

  const removeWs = name.split(' ');

  removeWs.shift();

  return removeWs.join(' ');
};

function PortfoliosList({
  portfolios,
  handlePortfolioChange,
  handleRadioChange,
  portfolioID,
  annuityRate,
  modelType,
  errors,
  ...rest
}) {
  return (
    <Grid {...rest}>
      {portfolios.map((port) => {
        const { percent, icon } = map[port.name];
        const selected = port.name === modelType;

        return (
          <Card
            key={port.name}
            className={selected ? 'selected' : ''}
            onClick={(e) =>
              handlePortfolioChange(e, {
                value: port.id.toString(),
                name: port.name,
              })
            }
          >
            <CardHeader>
              <Radio
                name={port.name}
                value={port.id}
                checked={selected}
                onChange={handlePortfolioChange}
              />
              <Icon name={icon} />
              <PortfolioName>{removeWsForUsVersion(port.name)}</PortfolioName>
            </CardHeader>

            <Bar>
              <BarEquity style={{ width: `${percent}%` }} />
            </Bar>
            <BarLabels>
              <EquityLabel>{percent}% Equity</EquityLabel>
              {percent < 100 && <BondLabel>{100 - percent}% Bonds</BondLabel>}
            </BarLabels>
          </Card>
        );
      })}

      <Card
        className={modelType === 'annuityRate' ? 'selected' : ''}
        onClick={(e) =>
          handlePortfolioChange(e, {
            value: null,
            name: 'annuityRate',
          })
        }
      >
        <CardHeader>
          <Radio
            name="annuityRate"
            value="annuityRate"
            checked={modelType === 'annuityRate'}
            onChange={handlePortfolioChange}
            className="radio-annuity"
          />
          <Icon name={map['Risk-free'].icon} />
          <PortfolioName>Risk-free</PortfolioName>
        </CardHeader>

        <RateRow>
          <InputWrapper>
            <StyledInput
              name="annuityRate"
              size="mini"
              value={!annuityRate && annuityRate !== 0 ? '' : annuityRate}
              onChange={handlePortfolioChange}
              disabled={modelType !== 'annuityRate'}
            />
            <PercentSign>%</PercentSign>
          </InputWrapper>
          <RateDescription>{map['Risk-free'].description}</RateDescription>
        </RateRow>

        <ErrorLabel
          style={{ top: '100%' }}
          className="bottom"
          msg={errors.portfolioID}
        />
      </Card>
    </Grid>
  );
}

PortfoliosList.propTypes = {
  portfolios: PropTypes.array.isRequired,
  handlePortfolioChange: PropTypes.func.isRequired,
  handleRadioChange: PropTypes.func.isRequired,
  portfolioID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  annuityRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  modelType: PropTypes.string,
  errors: PropTypes.object,
};

PortfoliosList.defaultProps = {
  annuityRate: null,
  portfolioID: null,
  modelType: '',
  errors: {},
};

export default PortfoliosList;

const SELECTED_BORDER = '#6c7cff';
const SELECTED_BG = '#f1f2ff';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 20px 26px 16px;
  background: #fff;
  border: 1px solid #e3e3e8;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: #c9ccf5;
  }

  &.selected {
    background: ${SELECTED_BG};
    border-color: ${SELECTED_BORDER};
  }

  &.selected .portfolio-name {
    font-weight: 700;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 18px;

  .ui.radio.checkbox {
    margin-right: 14px;
  }

  i.icon {
    margin: 0 12px 0 0;
    font-size: 1.1em;
    line-height: 1;
    width: auto;
    color: #333;
  }
`;

const PortfolioName = styled.span.attrs({ className: 'portfolio-name' })`
  font-size: 1.15rem;
  color: #333;
`;

const Bar = styled.div`
  display: flex;
  width: 100%;
  height: 20px;
  background: ${colors.periwinkle};
  border-radius: 6px;
  overflow: hidden;
`;

const BarEquity = styled.div`
  height: 100%;
  background: ${colors.navyBlue};
`;

const BarLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 0.95rem;
  font-weight: 700;
`;

const EquityLabel = styled.span`
  color: ${colors.navyBlue};
`;

const BondLabel = styled.span`
  color: ${colors.periwinkle};
`;

const RateRow = styled.div`
  display: flex;
  align-items: center;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 110px;
  flex-shrink: 0;
`;

const PercentSign = styled.span`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  pointer-events: none;
`;

const RateDescription = styled.span`
  margin-left: 16px;
  font-style: italic;
  color: #777;
`;

const StyledInput = styled(({ children, className, ...rest }) => (
  <Input className={className} {...rest}>
    {children}
  </Input>
))`
  width: 100%;

  input {
    height: 35px;
    padding-right: 34px !important;
    text-align: right;
    border: 1px solid #d4d4dc !important;
    border-radius: 6px !important;
  }

  &.disabled {
    opacity: 1 !important;
    input {
      opacity: 1 !important;
    }
  }
`;
